import React, { useState, useEffect } from "react";
import {
  Dialog,
  Button,
  Dropdown,
  TextInput,
  Image,
  Anchor,
} from "hesus-ui-kit";
import { withTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import PropTypes from "prop-types";
import ClipLoader from "react-spinners/ClipLoader";
import axios from "axios";
import { getDateFormat } from "@utils/datePickerUtils";
import {
  useGetMultiUserCurrenciesByCountryId,
  useGetGeneralParameters,
} from "@hooks/useGeneralParams";
import initMap from "@utils/map";
import { updateUnitTranslation } from "@utils/utils";
import {
  HeaderWrapper,
  Title,
  TransportQuoteInfoWrapper,
  ClientInfoWrapper,
  ClientName,
  SolutionInfoWrapper,
  TransportQuotationWrapper,
  MapContainer,
  AddressContainer,
  AddressLabel,
  Address,
  SolutionSummaryWrapper,
  TwoColumn,
  ThreeColumn,
  EditOptionsLabel,
  EditOptionsPreDefinedDescription,
  EditOptionsDescription,
  ButtonWrapper,
} from "./AddEditTransportQuotation.styled";
import "./AddEditTransportQuotation.scss";

import closeIcon from "@icons/ic-close-white.svg";

const AddEditTransportQuotation = (props) => {
  const { id, t, countryId, onClose } = props;

  const [unitList, setUnitList] = useState({});
  const hesusTransportsByIds = useSelector(
    (state) => state.transportQuotations.byId
  );
  const generalParameters = useGetGeneralParameters();
  const userCurrency = useGetMultiUserCurrenciesByCountryId(countryId);
  const unitObject = generalParameters?.unitList;

  useEffect(() => {
    if (unitObject?.length > 0) {
      setUnitList([{ label: t("SELECT_UNIT"), value: "" }, ...unitObject]);
    }
  }, [unitObject]);

  const [currencies, setCurrencies] = useState([]);
  const [formData, setFormData] = useState({
    quoteDetails: "",
    transportPrice: { value: "", required: true, error: "" },
    transportCurrency: {
      value: parseInt(countryId, 10),
      required: true,
      error: "",
    },
    transportUnit: { value: "", required: true, error: "" },
    trucksPerDay: "",
  });
  const [createSolutionAPIError, setCreateSolutionAPIError] = useState("");
  const [isTransportQuoteSaving, setTransportQuoteSaveStatus] = useState(false);

  const selectedTransport = hesusTransportsByIds[id];

  const TRANSPORT_PRICE = "transport-price";
  const TRANSPORT_CURRENCY = "transport-currency";
  const TRANSPORT_UNIT = "transport-unit";
  const LOADS_PER_DAY = "loads-per-day";
  const QUOTE_DETAILS = "details-for-quotation";

  const onChange = (e) => {
    const name = e.target?.name ? e.target.name : e.name;
    switch (name) {
      case TRANSPORT_PRICE:
        setFormData({
          ...formData,
          transportPrice: {
            ...formData.transportPrice,
            value:
              e.target.value === "" ||
              /^[0-9]*[.,]?\d{0,2}$/.test(e.target.value)
                ? e.target.value
                : formData.transportPrice.value,
          },
        });
        break;
      case TRANSPORT_CURRENCY:
        setFormData({
          ...formData,
          transportCurrency: {
            ...formData.transportCurrency,
            value: e.value,
          },
        });
        break;
      case TRANSPORT_UNIT:
        setFormData({
          ...formData,
          transportUnit: { ...formData.transportUnit, value: e.value },
        });
        break;
      case LOADS_PER_DAY:
        setFormData({
          ...formData,
          trucksPerDay:
            e.target.value === "" || /^[0-9]*$/.test(e.target.value)
              ? e.target.value
              : formData.trucksPerDay,
        });
        break;
      case QUOTE_DETAILS:
        setFormData({
          ...formData,
          quoteDetails: e.target.value,
        });
        break;
      default:
    }
  };

  const validateForm = (data) => {
    let isFormValid = true;
    const formDataObj = { ...data };

    if (!formDataObj.transportPrice.value) {
      isFormValid = false;
      formDataObj.transportPrice.error = t(
        "TRANSPORT_QUOTATION_PRICE_REQUIRED"
      );
    }

    if (!formDataObj.transportCurrency.value) {
      isFormValid = false;
      formDataObj.transportCurrency.error = t(
        "TRANSPORT_QUOTATION_CURRENCY_REQUIRED"
      );
    }

    if (!formDataObj.transportUnit.value) {
      isFormValid = false;
      formDataObj.transportUnit.error = t("TRANSPORT_QUOTATION_UNIT_REQUIRED");
    }

    if (!isFormValid) {
      setFormData({ ...formDataObj });
    }

    return isFormValid;
  };

  const getFormData = (data) => {
    return {
      price: Number(String(data.transportPrice.value).replace(",", ".")),
      unit: data.transportUnit.value,
      currency: { id: Number(data.transportCurrency.value) },
      loadsPerDay: data.trucksPerDay ? Number(data.trucksPerDay) : null,
      quoteComment: data.quoteDetails,
      selectedPrice: true,
      transportType: {
        id: selectedTransport.solutionTransportPrice?.transportType?.id ?? null,
      },
    };
  };

  const onSubmit = () => {
    const isFormValid = validateForm(formData);
    if (isFormValid) {
      const data = getFormData(formData);
      setTransportQuoteSaveStatus(true);
      setCreateSolutionAPIError("");
      // Create transport quotation
      if (
        selectedTransport.solutionTransportPrice?.id &&
        selectedTransport.solutionTransportPrice?.price === null &&
        selectedTransport.solutionTransportPrice?.unit === null
      ) {
        axios
          .put(
            `/solutions/${selectedTransport.id}/transportPrices/${selectedTransport.solutionTransportPrice.id}`,
            data
          )
          .then(() => {
            setTransportQuoteSaveStatus(false);
            onClose();
          })
          .catch((error) => {
            setTransportQuoteSaveStatus(false);
            setCreateSolutionAPIError(error.response?.data?.message ?? "");
          });
      } else {
        axios
          .post(`/solutions/${selectedTransport.id}/transportPrices`, data)
          .then(() => {
            setTransportQuoteSaveStatus(false);
            onClose();
          })
          .catch((error) => {
            setTransportQuoteSaveStatus(false);
            setCreateSolutionAPIError(error.response?.data?.message ?? "");
          });
      }
    }
  };

  const getProvisionalDate = () => {
    if (selectedTransport.site) {
      const date =
        selectedTransport.site.startDate && selectedTransport.site.endDate
          ? `${getDateFormat(
              selectedTransport.site.startDate,
              "YYYY-MM-DD"
            )} - ${getDateFormat(
              selectedTransport.site.endDate,
              "YYYY-MM-DD"
            )}`
          : "";
      const workingHours = selectedTransport.site.workingHours
        ? selectedTransport.site.workingHours.reduce(
            (prevValue, currentValue, index) => {
              const hour = selectedTransport.site.workingHours[index];
              return (prevValue += `${hour.openingHour}-${hour.closingHour}`);
            },
            ""
          )
        : "";
      return `${date}, ${workingHours}`;
    }
    return "";
  };

  useEffect(() => {
    if (Object.entries(userCurrency)?.length > 0) {
      const currenciesList = Object.entries(userCurrency).map((entry) => {
        return { label: entry[1], value: entry[0] };
      });
      setCurrencies(currenciesList);
    }
  }, []);

  useEffect(() => {
    initMap(
      "map",
      selectedTransport.departure.position.latitude,
      selectedTransport.departure.position.longitude,
      selectedTransport.destination.position.latitude,
      selectedTransport.destination.position.longitude,
      true
    );
  }, [
    selectedTransport.departure.position.latitude,
    selectedTransport.departure.position.longitude,
    selectedTransport.destination.position.latitude,
    selectedTransport.destination.position.longitude,
  ]);

  useEffect(() => {
    if (selectedTransport.solutionTransportPrice) {
      setFormData({
        ...formData,
        transportPrice: {
          ...formData.transportPrice,
          value: selectedTransport.solutionTransportPrice.price ?? "",
        },
        transportCurrency: {
          ...formData.transportCurrency,
          value: selectedTransport.solutionTransportPrice.currency?.id ?? "",
        },
        transportUnit: {
          ...formData.transportUnit,
          value: selectedTransport.solutionTransportPrice.unit ?? "",
        },
        quoteDetails: selectedTransport.solutionTransportPrice.internalComment,
        trucksPerDay:
          selectedTransport.solutionTransportPrice.loadsPerDay ?? "",
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTransport.solutionTransportPrice]);

  return (
    <Dialog>
      <>
        <HeaderWrapper
          backgroundColor={
            selectedTransport.priceType === 2 ? "#FF5200" : "#15aee5"
          }
        >
          <Title>
            {selectedTransport.priceType !== 2 && t("ADD_TRANSPORT_QUOTATION")}
            {selectedTransport.priceType === 2 &&
              t("ADD_TRANSPORT_QUOTATION_TP")}
          </Title>
          <Button
            onClick={onClose}
            styles={{
              backgroundColor: "transparent",
              border: "none",
              height: "auto",
              padding: 0,
              "&:hover": {
                backgroundColor: "transparent",
              },
            }}
          >
            <Image
              src={closeIcon}
              alt="close selling price popup"
              styles={{ width: "auto" }}
            />
          </Button>
        </HeaderWrapper>
        <TransportQuoteInfoWrapper>
          <ClientInfoWrapper>
            {selectedTransport.site?.companyName && (
              <ClientName>
                {t("CLIENT")}:{" "}
                <Anchor
                  url={`/company/view/${selectedTransport.site.companyId}/general-info`}
                  target="_blank"
                  rel="noreferrer"
                  className="add-edit-transport-quotation__address-link"
                >
                  {selectedTransport.site.companyName}
                </Anchor>
              </ClientName>
            )}
            <SolutionInfoWrapper>
              {selectedTransport.enquiry?.id && (
                <div>
                  {t("REQUEST_ID")} :
                  <Anchor
                    url={`/site/view/${selectedTransport.site.id}/enquiry-view-${selectedTransport.enquiry.id}`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {selectedTransport.enquiry.id}
                  </Anchor>
                </div>
              )}
              <div>
                {t("MATCHING_ID")} :{selectedTransport.id}
              </div>
            </SolutionInfoWrapper>
          </ClientInfoWrapper>
          <AddressContainer>
            <div>
              <AddressLabel>{t("DEPARTURE")}</AddressLabel>
              <Anchor
                url={
                  selectedTransport.enquiry.service === "EVACUATION"
                    ? `/site/view/${selectedTransport.site.id}/general-info`
                    : `/company/view/${selectedTransport.centre?.companyId}/general-info`
                }
                target="_blank"
                rel="noreferrer"
              >
                {selectedTransport.enquiry.service === "EVACUATION" &&
                  selectedTransport.site?.name}
                {selectedTransport.enquiry.service === "DELIVERY" &&
                  selectedTransport.centre?.companyName}
              </Anchor>
              <Address>
                {`${selectedTransport.departure.street}, ${selectedTransport.departure.city}, ${selectedTransport.departure.postalCode}`}
              </Address>
            </div>
            <div>
              <AddressLabel>{t("DESTINATION")}</AddressLabel>
              <Anchor
                url={
                  selectedTransport.enquiry.service === "DELIVERY"
                    ? `/site/view/${selectedTransport.site.id}/general-info`
                    : `/company/view/${selectedTransport.centre?.companyId}/general-info`
                }
                target="_blank"
                rel="noreferrer"
              >
                {selectedTransport.enquiry.service === "DELIVERY" &&
                  selectedTransport.site?.name}
                {selectedTransport.enquiry.service === "EVACUATION" &&
                  selectedTransport.centre?.companyName}
              </Anchor>
              <Address>
                {`${selectedTransport.destination.street}, ${selectedTransport.destination.city}, ${selectedTransport.destination.postalCode}`}
              </Address>
            </div>
          </AddressContainer>
          <SolutionSummaryWrapper>
            <TwoColumn>
              <TextInput
                type="text"
                label={t("PROVISIONAL_DATES_AND_SCHEDULES")}
                name="provisional-date-time"
                id="provisional-date-time"
                isFloat
                value={getProvisionalDate()}
                onChange={() => {}}
                containerStyles={{ marginBottom: "16px", marginTop: "16px" }}
                inputStyles={{ padding: "7px 5px 7px 10px" }}
                disabled
              />
              <TextInput
                type="text"
                label={t("TRANSPORT_QUOTATION_POP_UP_TRANSPORT_ONLY_TRANSPORT")}
                name="transport"
                id="transport"
                isFloat
                value={
                  selectedTransport.solutionTransportPrice?.transportType
                    ?.name ?? ""
                }
                onChange={() => {}}
                containerStyles={{ marginBottom: "16px", marginTop: "16px" }}
                inputStyles={{ padding: "7px 5px 7px 10px" }}
                disabled
              />
            </TwoColumn>

            <TwoColumn>
              <TextInput
                type="text"
                label={t("QUANTITY")}
                name="quantity"
                id="quantity"
                isFloat
                value={`${selectedTransport.enquiry.quantity} ${t(
                  selectedTransport.enquiry.unit
                )}`}
                onChange={() => {}}
                inputStyles={{ padding: "7px 5px 7px 10px" }}
                disabled
              />
              <TextInput
                type="text"
                label={t("PROVISIONAL_TRUCK_PER_DAY")}
                name="provisional-trucks-per-day"
                id="provisional-trucks-per-day"
                isFloat
                value={selectedTransport.enquiry.truckPerDay}
                onChange={() => {}}
                containerStyles={{ marginBottom: "16px" }}
                inputStyles={{ padding: "7px 5px 7px 10px" }}
                disabled
              />
            </TwoColumn>
            <TwoColumn>
              <TextInput
                type="text"
                label={t("PRODUCT")}
                name="product"
                id="product"
                isFloat
                value={selectedTransport.centreProduct?.product?.name ?? ""}
                onChange={() => {}}
                containerStyles={{ marginBottom: "16px" }}
                inputStyles={{ padding: "7px 5px 7px 10px" }}
                disabled
              />
            </TwoColumn>
            <TextInput
              type="text"
              label={t(
                "TRANSPORT_QUOTATION_POP_UP_TRANSPORT_ONLY_DETAILS_QUOTATION"
              )}
              name={QUOTE_DETAILS}
              id={QUOTE_DETAILS}
              isFloat
              value={formData.quoteDetails}
              onChange={onChange}
              containerStyles={{ marginBottom: "16px", marginTop: "16px" }}
              inputStyles={{ padding: "7px 5px 7px 10px" }}
            />
            <div>
              <div>
                <EditOptionsLabel>
                  {t(
                    "TRANSPORT_QUOTATION_POP_UP_TRANSPORT_ONLY_ACCEPTANCE_REQUIREMENTS"
                  )}
                </EditOptionsLabel>
                <EditOptionsDescription>
                  {selectedTransport.centreProduct?.acceptanceRequirements ??
                    ""}
                </EditOptionsDescription>
                <EditOptionsDescription>
                  {selectedTransport.centreProduct?.acceptanceSpecificities ??
                    ""}
                </EditOptionsDescription>
              </div>
              <div>
                <EditOptionsLabel>
                  {t("TRANSPORT_QUOTATION_POP_UP_TRANSPORT_ONLY_ALERT")}
                </EditOptionsLabel>
                {selectedTransport.centreProduct?.comment && (
                  <EditOptionsPreDefinedDescription>
                    {selectedTransport.centreProduct.comment}
                  </EditOptionsPreDefinedDescription>
                )}
              </div>
            </div>
          </SolutionSummaryWrapper>
          <TransportQuotationWrapper>
            <ThreeColumn>
              <TextInput
                type="text"
                label={t("PRICE")}
                name={TRANSPORT_PRICE}
                id={TRANSPORT_PRICE}
                isFloat
                value={formData.transportPrice.value}
                onChange={onChange}
                required
                error={formData.transportPrice.error}
                containerStyles={{ marginTop: "25px" }}
                inputStyles={{ padding: "7px 5px 7px 10px" }}
              />
              <Dropdown
                label={t("GENERAL_CURENCY")}
                name={TRANSPORT_CURRENCY}
                value={String(formData.transportCurrency.value)}
                defaultOption={t("SELECT_CURRENCY")}
                inputStyles={{ border: "1px solid #474747" }}
                options={currencies}
                error={formData.transportCurrency.error}
                required
                onChange={onChange}
              />
              <Dropdown
                label={t("UNIT")}
                name={TRANSPORT_UNIT}
                value={String(formData.transportUnit.value)}
                defaultOption={t("NONE_SELECTED")}
                inputStyles={{ border: "1px solid #474747" }}
                options={updateUnitTranslation(unitList, t)}
                required
                error={formData.transportUnit.error}
                onChange={onChange}
              />
              <TextInput
                type="text"
                label={t("LOADS_PER_DAY")}
                name={LOADS_PER_DAY}
                id={LOADS_PER_DAY}
                isFloat
                value={formData.trucksPerDay}
                onChange={onChange}
                containerStyles={{ marginBottom: "16px", marginTop: "16px" }}
                inputStyles={{ padding: "7px 5px 7px 10px" }}
              />
            </ThreeColumn>
          </TransportQuotationWrapper>
          <MapContainer id="map" />
        </TransportQuoteInfoWrapper>
        <ButtonWrapper>
          {createSolutionAPIError && <p>{createSolutionAPIError}</p>}
          <Button
            styles={{
              color: "white",
              background: "#1BACE4",
              border: "1px solid #1BACE4",
              marginRight: "5px",
              "&:hover": {
                backgroundColor: "#1589b6",
              },
            }}
            disabled={isTransportQuoteSaving}
            onClick={onSubmit}
          >
            <>
              {isTransportQuoteSaving && <ClipLoader size={15} color="#fff" />}
              {!isTransportQuoteSaving && t("SUBMIT")}
            </>
          </Button>
          <Button
            styles={{
              color: "#333",
              background: "#fff",
              border: "1px solid #ccc",
              "&:hover": {
                backgroundColor: "#ccc",
              },
            }}
            onClick={onClose}
          >
            <>{t("CLOSE")}</>
          </Button>
        </ButtonWrapper>
      </>
    </Dialog>
  );
};

AddEditTransportQuotation.propTypes = {
  id: PropTypes.number.isRequired,
  onClose: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
  countryId: PropTypes.number.isRequired,
};

export default withTranslation()(AddEditTransportQuotation);
